/**
 * A person's own mail preferences (piece 32; T419, decision 1236): how the
 * mailbox draws and what a composed message starts with. 🔒 One Person-scoped
 * `SettingValue` row, `mail.preferences`, with no Organization default: an
 * absent row, or an absent member, is the client exactly as it was before the
 * row existed. The Mail Settings window's View Options and Compose pages write
 * it for their own person and nobody else's.
 */
import { MAIL_API_PATH } from './client';

export const MAIL_PREFERENCES_KEY = 'mail.preferences';

/** Where the reading pane stands: beside the list, as it always has, or beneath it. */
export const READING_PANE_PLACES = ['Right', 'Bottom'] as const;
export type ReadingPanePlace = (typeof READING_PANE_PLACES)[number];
/** How a received body is drawn: as the sender formatted it, or as plain text with every picture and style left out. */
export const READING_FORMATS = ['Rich', 'PlainText'] as const;
export type ReadingFormat = (typeof READING_FORMATS)[number];
/** What the reading pane opens once the open message is deleted or moved away: the next one down, the one above, or nothing. */
export const AFTER_DELETE_CHOICES = ['List', 'Next', 'Previous'] as const;
export type AfterDeleteChoice = (typeof AFTER_DELETE_CHOICES)[number];
/** Which mailbox a reply goes from: the one the message is in where the person may send from it, or always their own. */
export const REPLY_FROM_CHOICES = ['ReceivingMailbox', 'OwnMailbox'] as const;
export type ReplyFromChoice = (typeof REPLY_FROM_CHOICES)[number];
/** The line above quoted history: one line naming the date and the sender, or the full From, Sent, To and Subject block. */
export const REPLY_HEADER_STYLES = ['Short', 'Full'] as const;
export type ReplyHeaderStyle = (typeof REPLY_HEADER_STYLES)[number];
/** Where a reply's signature stands: above the quoted history, as it always has, or below it. */
export const SIGNATURE_PLACEMENTS = ['AboveQuote', 'BelowQuote'] as const;
export type SignaturePlacement = (typeof SIGNATURE_PLACEMENTS)[number];

export interface MailPreferences {
  readingPane: ReadingPanePlace;
  readingFormat: ReadingFormat;
  /** Every message composed asks for a read receipt unless the person unticks it on that message. */
  askReadReceipt: boolean;
  afterDelete: AfterDeleteChoice;
  replyFrom: ReplyFromChoice;
  replyHeader: ReplyHeaderStyle;
  signaturePlacement: SignaturePlacement;
}

/** 🔒 The client as it was: every member's first choice is the behaviour before the row existed. */
export const DEFAULT_MAIL_PREFERENCES: Readonly<MailPreferences> = {
  readingPane: 'Right',
  readingFormat: 'Rich',
  askReadReceipt: false,
  afterDelete: 'List',
  replyFrom: 'ReceivingMailbox',
  replyHeader: 'Short',
  signaturePlacement: 'AboveQuote',
};

const CHOICES: { readonly [K in keyof MailPreferences]: readonly MailPreferences[K][] } = {
  readingPane: READING_PANE_PLACES,
  readingFormat: READING_FORMATS,
  askReadReceipt: [true, false],
  afterDelete: AFTER_DELETE_CHOICES,
  replyFrom: REPLY_FROM_CHOICES,
  replyHeader: REPLY_HEADER_STYLES,
  signaturePlacement: SIGNATURE_PLACEMENTS,
};

const has = <K extends keyof MailPreferences>(key: K, value: unknown): value is MailPreferences[K] => (CHOICES[key] as readonly unknown[]).includes(value);

/** The stored row read as preferences: a member it does not hold, or holds outside its choices, reads as the client did before. */
export function mailPreferencesFrom(stored: unknown): MailPreferences {
  const row = stored && typeof stored === 'object' && !Array.isArray(stored) ? (stored as Record<string, unknown>) : {};
  const out = { ...DEFAULT_MAIL_PREFERENCES } as MailPreferences;
  for (const key of Object.keys(CHOICES) as (keyof MailPreferences)[]) {
    if (has(key, row[key])) (out as unknown as Record<string, unknown>)[key] = row[key];
  }
  return out;
}

/** Why a change to the preferences is refused, in a sentence, or null. A member it does not name is left as it stands. */
export function mailPreferencesProblem(input: unknown): string | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return 'The preferences arrive as named choices.';
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (!(key in CHOICES)) return `The mail preferences hold no choice called ${key}.`;
    if (!has(key as keyof MailPreferences, value)) return `The ${key} choice is not one this client offers.`;
  }
  return null;
}

/** The View Options, Compose and Conversation pages' one read and one write. */
export const MAIL_PERSONAL_SETTINGS_PATH = `${MAIL_API_PATH}/settings/personal`;

export interface PersonalMailSettingsAnswer {
  preferences: MailPreferences;
  /** The person's own undo-send delay in seconds, or null where they send under the organisation's (decision 0833). */
  undoDelaySeconds: number | null;
  /** The organisation's delay, which applies while the person keeps none of their own. */
  undoDelayDefault: number;
}

export interface PersonalMailSettingsInput {
  preferences?: Partial<MailPreferences>;
  /** The person's own undo delay; null returns them to the organisation's, and absent leaves it as it stands. */
  undoDelaySeconds?: number | null;
}
